/**
 * BURA Theme - Global JavaScript
 * Sklep z akcesoriami dla psow - Adapted for BlueTruck
 */

// ============================================================================
// Utility Functions
// ============================================================================

const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

const formatMoney = (cents, format) => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.theme.moneyFormat || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision || 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) return 0;

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

const fetchConfig = (type = 'json') => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
};

// ============================================================================
// Mobile Menu
// ============================================================================

class MobileMenu {
  constructor() {
    this.menu = document.querySelector('[data-mobile-menu]');
    if (!this.menu) return;

    this.toggleBtn = document.querySelector('[data-menu-toggle]');
    this.closeBtns = this.menu.querySelectorAll('[data-mobile-menu-close]');
    this.expandBtns = this.menu.querySelectorAll('.mobile-menu__expand');

    // Search elements
    this.searchInput = this.menu.querySelector('[data-mobile-search-input]');
    this.searchResults = this.menu.querySelector('[data-mobile-search-results]');
    this.searchForm = this.menu.querySelector('.mobile-menu__search-form');

    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtn?.addEventListener('click', () => this.open());

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    this.expandBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const item = e.target.closest('.mobile-menu__item');
        item.classList.toggle('is-expanded');
      });
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
        this.close();
      }
    });

    // Search events
    if (this.searchInput) {
      this.searchInput.addEventListener('input', debounce((e) => {
        this.performSearch(e.target.value);
      }, 300));

      this.searchInput.addEventListener('focus', () => {
        if (this.searchInput.value.length >= 2) {
          this.searchResults.classList.add('has-results');
        }
      });
    }

    this.searchForm?.addEventListener('submit', (e) => {
      // Allow default submission
    });
  }

  open() {
    this.menu.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      this.searchInput?.focus();
    }, 100);
  }

  close() {
    this.menu.classList.remove('is-open');
    document.body.style.overflow = '';
    if (this.searchResults) {
      this.searchResults.classList.remove('has-results');
    }
  }

  async performSearch(query) {
    if (!this.searchResults) return;

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      this.searchResults.innerHTML = '';
      this.searchResults.classList.remove('has-results');
      return;
    }

    try {
      const response = await fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(trimmedQuery)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`
      );

      if (!response.ok) throw new Error('Search request failed');

      const text = await response.text();
      const resultsParser = new DOMParser().parseFromString(text, 'text/html');
      const resultsMarkup = resultsParser.querySelector('#shopify-section-predictive-search');

      if (resultsMarkup) {
        const products = Array.from(resultsMarkup.querySelectorAll('a')).map(a => {
          const img = a.querySelector('img');
          const title = a.querySelector('.predictive-search__item-heading')?.textContent || a.textContent;
          const price = a.querySelector('.price') || a.querySelector('.predictive-search__item-price');
          return {
            url: a.href,
            title: title ? title.trim() : '',
            image: img ? img.src : null,
            price: price ? price.textContent.trim() : ''
          };
        });

        this.renderResults(products, trimmedQuery);
      }
    } catch (error) {
      console.error('Mobile search error:', error);
      this.searchResults.innerHTML = `<div class="mobile-search-no-results">Error loading results</div>`;
      this.searchResults.classList.add('has-results');
    }
  }

  renderResults(products, query) {
    if (products.length === 0) {
      this.searchResults.innerHTML = `<div class="mobile-search-no-results">No products found for "${query}"</div>`;
    } else {
      const html = products.map(product => `
          <a href="${product.url}" class="mobile-search-result">
            <div class="mobile-search-result__image">
              ${product.image ? `<img src="${product.image}" alt="${product.title}">` : ''}
            </div>
            <div class="mobile-search-result__info">
              <span class="mobile-search-result__title">${product.title}</span>
              <span class="mobile-search-result__price">${product.price}</span>
            </div>
          </a>
        `).join('');

      const viewAllHtml = `
          <a href="${window.routes.search_url}?q=${encodeURIComponent(query)}&type=product" class="mobile-search-view-all">
            View all results for "${query}"
          </a>
        `;

      this.searchResults.innerHTML = html + viewAllHtml;
    }
    this.searchResults.classList.add('has-results');
  }
}

class FilterDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-filter-drawer]');
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-filter-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        this.open();
        return;
      }

      const closeBtn = e.target.closest('[data-filter-close]');
      if (closeBtn && this.drawer && this.drawer.classList.contains('is-open')) {
        e.preventDefault();
        this.close();
      }
    });

    if (this.drawer) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });
    }
  }

  open() {
    if (this.drawer) {
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    if (this.drawer) {
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }
}

// ============================================================================
// Search
// ============================================================================

class Search {
  constructor() {
    this.overlay = document.querySelector('[data-search-overlay]');
    if (!this.overlay) return;

    this.toggleBtns = document.querySelectorAll('[data-search-toggle]');
    this.closeBtn = this.overlay.querySelector('[data-search-close]');
    this.input = this.overlay.querySelector('[data-search-input]');
    this.results = this.overlay.querySelector('[data-search-results]');

    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => this.toggle());
    });

    this.closeBtn?.addEventListener('click', () => this.close());

    this.input?.addEventListener('input', debounce((e) => {
      this.search(e.target.value);
    }, 300));

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.overlay.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.overlay.classList.add('is-open');
    this.input?.focus();
  }

  close() {
    this.overlay.classList.remove('is-open');
    if (this.input) this.input.value = '';
    if (this.results) this.results.innerHTML = '';
  }

  async search(query) {
    if (query.length < 2) {
      this.results.innerHTML = '';
      return;
    }

    try {
      const response = await fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product&resources[limit]=4`
      );

      if (!response.ok) throw new Error('Search failed');

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const resultsMarkup = html.querySelector('#shopify-section-predictive-search')?.innerHTML;

      if (resultsMarkup) {
        this.results.innerHTML = resultsMarkup;
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  }
}

// ============================================================================
// Cart Drawer
// ============================================================================

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.toggleBtns = document.querySelectorAll('[data-cart-toggle]');
    this.closeBtns = this.drawer.querySelectorAll('[data-cart-drawer-close]');
    this.content = this.drawer.querySelector('[data-cart-drawer-content]');

    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Quantity buttons
    this.drawer.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('[data-cart-qty-minus]');
      const plusBtn = e.target.closest('[data-cart-qty-plus]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (minusBtn) {
        const key = minusBtn.dataset.cartQtyMinus;
        const input = this.drawer.querySelector(`[data-cart-qty-input="${key}"]`);
        const newQty = Math.max(0, parseInt(input.value) - 1);
        this.updateItem(key, newQty);
      }

      if (plusBtn) {
        const key = plusBtn.dataset.cartQtyPlus;
        const input = this.drawer.querySelector(`[data-cart-qty-input="${key}"]`);
        const newQty = parseInt(input.value) + 1;
        this.updateItem(key, newQty);
      }

      if (removeBtn) {
        const key = removeBtn.dataset.cartRemove;
        this.updateItem(key, 0);
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    // Listen for add to cart events
    document.addEventListener('cart:add', () => {
      this.refresh();
      this.open();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async updateItem(key, quantity) {
    try {
      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Update failed');

      const cart = await response.json();
      this.updateCartUI(cart);
      this.refresh();
    } catch (error) {
      console.error('Cart update error:', error);
    }
  }

  async refresh() {
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newContent = html.querySelector('[data-cart-drawer-content]');

      if (newContent && this.content) {
        this.content.innerHTML = newContent.innerHTML;
      }

      // Re-fetch full cart object to update headers correctly
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      this.updateCartUI(cart);

    } catch (error) {
      console.error('Cart refresh error:', error);
    }
  }

  updateCartUI(cart) {
    // 1. Update all count elements (Header bubble, Drawer title)
    const countEls = document.querySelectorAll('[data-cart-count], [data-cart-drawer-count]');
    countEls.forEach(el => {
      const count = cart.item_count;
      // Handle the parenthetical format if it's the drawer count
      if (el.hasAttribute('data-cart-drawer-count')) {
        el.textContent = `(${count})`;
      } else {
        el.textContent = count;
      }

      // Toggle visibility for header bubble
      if (el.classList.contains('header__cart-count')) {
        el.classList.toggle('is-hidden', count == 0);
      }
    });

    // 2. Update cart totals (Header price + Drawer subtotal)
    const totalEls = document.querySelectorAll('[data-cart-total], [data-cart-subtotal]');
    totalEls.forEach(el => {
      el.textContent = formatMoney(cart.total_price);
    });
  }
}

// ============================================================================
// Add to Cart
// ============================================================================

class AddToCart {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;

      e.preventDefault();
      const variantId = btn.dataset.addToCart;

      btn.disabled = true;
      btn.classList.add('is-loading');

      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }]
          })
        });

        if (!response.ok) throw new Error('Add to cart failed');

        await this.refreshHeaderCart();
        document.dispatchEvent(new CustomEvent('cart:add'));
      } catch (error) {
        console.error('Add to cart error:', error);
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    });
  }

  async refreshHeaderCart() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-hidden', cart.item_count === 0);
      });
      document.querySelectorAll('[data-cart-total]').forEach(el => {
        el.textContent = this.formatMoney(cart.total_price);
      });
    } catch (e) {
      console.error('Header cart refresh error:', e);
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }
}

// ============================================================================
// Sticky Header
// ============================================================================

class StickyHeader {
  constructor() {
    this.header = document.querySelector('[data-header]');
    if (!this.header || !this.header.classList.contains('header--sticky')) return;

    this.lastScroll = 0;
    this.bindEvents();
  }

  bindEvents() {
    window.addEventListener('scroll', debounce(() => {
      const currentScroll = window.scrollY;

      if (currentScroll > 100) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }

      this.lastScroll = currentScroll;
    }, 10));
  }
}

// ============================================================================
// Announcement Bar Slider
// ============================================================================

class AnnouncementSlider {
  constructor() {
    this.slider = document.querySelector('[data-slider]');
    if (!this.slider) return;

    this.slides = this.slider.querySelectorAll('.announcement-bar__slide');
    if (this.slides.length <= 1) return;

    this.currentIndex = 0;
    this.interval = null;

    this.init();
  }

  init() {
    // Hide all slides except first
    this.slides.forEach((slide, index) => {
      if (index !== 0) slide.style.display = 'none';
    });

    // Start auto rotation
    this.startRotation();

    // Bind navigation buttons
    const prevBtn = document.querySelector('.announcement-bar__prev');
    const nextBtn = document.querySelector('.announcement-bar__next');

    prevBtn?.addEventListener('click', () => {
      this.prev();
      this.restartRotation();
    });

    nextBtn?.addEventListener('click', () => {
      this.next();
      this.restartRotation();
    });
  }

  startRotation() {
    this.interval = setInterval(() => this.next(), 5000);
  }

  restartRotation() {
    clearInterval(this.interval);
    this.startRotation();
  }

  next() {
    this.slides[this.currentIndex].style.display = 'none';
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.slides[this.currentIndex].style.display = 'flex';
  }

  prev() {
    this.slides[this.currentIndex].style.display = 'none';
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.slides[this.currentIndex].style.display = 'flex';
  }
}

// ============================================================================
// Notification System
// ============================================================================

class Notification {
  constructor() {
    this.container = this.createContainer();
  }

  createContainer() {
    let container = document.querySelector('.notification');
    if (!container) {
      container = document.createElement('div');
      container.className = 'notification';
      document.body.appendChild(container);
    }
    return container;
  }

  show(message, type = 'default', duration = 3000) {
    this.container.textContent = message;
    this.container.className = 'notification';

    if (type === 'success') {
      this.container.classList.add('notification--success');
    } else if (type === 'error') {
      this.container.classList.add('notification--error');
    }

    // Force reflow for animation
    this.container.offsetHeight;
    this.container.classList.add('is-visible');

    setTimeout(() => {
      this.container.classList.remove('is-visible');
    }, duration);
  }
}

// Global notification instance
window.notification = new Notification();

// ============================================================================
// Scroll Reveal (animations.css [data-reveal])
// ============================================================================

class ScrollReveal {
  constructor() {
    this.elements = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (!this.elements.length) return;

    if ('IntersectionObserver' in window) {
      this.initObserver();
    } else {
      // Fallback: reveal everything immediately
      this.elements.forEach(el => el.classList.add('is-revealed'));
    }
  }

  initObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -50px 0px', threshold: 0.1 });

    this.elements.forEach(el => observer.observe(el));
  }
}

// ============================================================================
// Image Lazy Loading Enhancement
// ============================================================================

class LazyImages {
  constructor() {
    this.images = document.querySelectorAll('img[loading="lazy"]');
    if (!this.images.length) return;

    if ('IntersectionObserver' in window) {
      this.initObserver();
    }
  }

  initObserver() {
    const options = {
      root: null,
      rootMargin: '50px 0px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.classList.add('is-loaded');
          observer.unobserve(img);
        }
      });
    }, options);

    this.images.forEach(img => observer.observe(img));
  }
}

// ============================================================================
// Smooth Scroll
// ============================================================================

class SmoothScroll {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;

      const targetId = anchor.getAttribute('href');
      if (targetId === '#') return;

      const target = document.querySelector(targetId);
      if (!target) return;

      e.preventDefault();
      const headerHeight = document.querySelector('[data-header]')?.offsetHeight || 0;
      const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    });
  }
}

// ============================================================================
// Cart Main (full cart page /cart)
// ============================================================================

class CartMain {
  constructor() {
    this.root = document.querySelector('.cart-main');
    if (!this.root) return;
    this.section = this.root.closest('[id^="shopify-section-"]');
    this.sectionId = this.section?.id?.replace('shopify-section-', '');
    this.bindEvents();
  }

  bindEvents() {
    this.root.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('[data-cart-qty-minus]');
      const plusBtn = e.target.closest('[data-cart-qty-plus]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (minusBtn) {
        e.preventDefault();
        const key = minusBtn.dataset.cartQtyMinus;
        const input = this.root.querySelector(`[data-cart-qty-input="${key}"]`);
        const newQty = Math.max(0, parseInt(input.value) - 1);
        this.updateItem(key, newQty);
      }

      if (plusBtn) {
        e.preventDefault();
        const key = plusBtn.dataset.cartQtyPlus;
        const input = this.root.querySelector(`[data-cart-qty-input="${key}"]`);
        const newQty = parseInt(input.value) + 1;
        this.updateItem(key, newQty);
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.cartRemove;
        this.updateItem(key, 0);
      }
    });

    this.root.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      const key = input.getAttribute('data-cart-qty-input');
      const newQty = Math.max(0, parseInt(input.value) || 0);
      this.updateItem(key, newQty);
    });
  }

  setLoading(state) {
    if (state) {
      this.root.style.pointerEvents = 'none';
      this.root.style.opacity = '0.6';
      this.root.style.transition = 'opacity 0.15s ease';
    } else {
      this.root.style.pointerEvents = '';
      this.root.style.opacity = '';
    }
  }

  async updateItem(key, quantity) {
    try {
      this.setLoading(true);

      const sections = this.sectionId ? [this.sectionId] : [];
      const body = {
        id: key,
        quantity,
        sections: sections.join(','),
        sections_url: window.location.pathname
      };

      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Update failed');
      const cart = await response.json();

      // Pusty koszyk - pelny reload zeby pokazal empty state
      if (cart.item_count === 0) {
        window.location.reload();
        return;
      }

      // Inline update sekcji cart-main
      if (cart.sections && this.sectionId && cart.sections[this.sectionId]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cart.sections[this.sectionId], 'text/html');
        const newRoot = doc.querySelector('.cart-main');
        if (newRoot) {
          this.root.innerHTML = newRoot.innerHTML;
        }
      }

      // Update header cart count i total
      this.updateHeaderCart(cart);

      this.setLoading(false);
    } catch (error) {
      console.error('Cart main update error:', error);
      this.setLoading(false);
    }
  }

  updateHeaderCart(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.classList.toggle('is-hidden', cart.item_count === 0);
    });
    document.querySelectorAll('[data-cart-total]').forEach(el => {
      el.textContent = this.formatMoney(cart.total_price);
    });
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }
}

// ============================================================================
// Initialize
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
  new Search();
  new CartDrawer();
  new CartMain();
  new AddToCart();
  new StickyHeader();
  new AnnouncementSlider();
  new ScrollReveal();
  new LazyImages();
  new SmoothScroll();
  new FilterDrawer();
});

// Listen for successful cart add to show notification
document.addEventListener('cart:add', () => {
  if (window.notification) {
    window.notification.show('Product added to cart', 'success');
  }
});